import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Stack,
} from '@mui/material';
import { AddCircle, DeleteOutline, VerifiedUser } from '@mui/icons-material';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, Vendor } from '../../services/procurementService';
import { projectService, Project } from '../../services/projectService';
import { RequisitionPriority, RequisitionType } from './PurchaseRequisitionPage';
import {
  P,
  cardSx,
  labelSx,
  fieldSx,
  priBtn,
  outBtn,
  gridInput,
  thSx,
  thTitle,
  PX,
  FieldLabel,
  StatusPill,
  WorkspaceHeader,
  TotalsStrip,
  ValidationStrip,
} from '../../components/common/workspaceKit';

interface Warehouse {
  id: number | string;
  warehouseCode?: string;
  warehouseName: string;
}

// ---------------------------------------------------------------------------
// Reference-data shapes, matching the conventions PurchaseOrderPage already
// uses for the same endpoints.
// ---------------------------------------------------------------------------

interface Product {
  id: string;
  product_name: string;
  hsn_code: string;
  unit: string;
  gst_rate: number;
  purchase_price?: number;
  is_active?: boolean;
}

interface CostCenter {
  id: number;
  code: string;
  name: string;
  isActive?: boolean;
}

interface RequisitionItemFromApi {
  id: number;
  product_id: string;
  product_name?: string;
  quantity: number | string;
  uom?: string | null;
  estimated_unit_cost: number | string;
  specification?: string | null;
  preferred_vendor_id?: string | null;
  preferred_vendor_name?: string | null;
}

interface RequisitionFromApi {
  id: number;
  requisition_date: string;
  requisition_type: RequisitionType;
  department: string | null;
  cost_center_id: number | string | null;
  project_id: number | string | null;
  warehouse_id: number | string | null;
  priority: RequisitionPriority;
  required_by_date: string | null;
  justification: string | null;
  status: string;
  items: RequisitionItemFromApi[];
}

const REQUISITION_TYPES: RequisitionType[] = [
  'MATERIAL',
  'SERVICE',
  'CAPEX',
  'SUBCONTRACTING',
  'JOB_WORK',
  'CONSUMABLE',
  'ASSET',
  'OTHER',
];

const PRIORITIES: RequisitionPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface ItemDraft {
  key: string;
  itemId?: number;
  product: Product | null;
  quantity: string;
  uom: string;
  estimatedUnitCost: string;
  specification: string;
  preferredVendor: Vendor | null;
}

const emptyItem = (): ItemDraft => ({
  key: nextDraftKey(),
  product: null,
  quantity: '1',
  uom: '',
  estimatedUnitCost: '',
  specification: '',
  preferredVendor: null,
});

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const PurchaseRequisitionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [products, setProducts] = useState<Product[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingRequisition, setLoadingRequisition] = useState(isEdit);

  const [requisitionDate, setRequisitionDate] = useState(todayISO());
  const [requisitionType, setRequisitionType] = useState<RequisitionType>('MATERIAL');
  const [department, setDepartment] = useState('');
  const [priority, setPriority] = useState<RequisitionPriority>('MEDIUM');
  const [requiredByDate, setRequiredByDate] = useState('');
  const [costCenterId, setCostCenterId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [justification, setJustification] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load reference data (products, vendors, cost centers) once.
  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      api.get<{ products: Product[] }>('/products').catch(() => ({ products: [] as Product[] })),
      procurementService.getVendors().catch(() => ({ vendors: [] as Vendor[], count: 0 })),
      // Cost centers / projects / warehouses are optional context (Finance
      // Workspace / Projects / Inventory masters) -- a tenant without that
      // module enabled, or the endpoint being unavailable, must not block
      // raising a requisition.
      api.get<{ costCenters: CostCenter[] }>('/cost-centers').catch(() => ({ costCenters: [] as CostCenter[] })),
      projectService.getProjects().catch(() => [] as Project[]),
      api.get<{ warehouses: Warehouse[] }>('/inventory/warehouses').catch(() => ({ warehouses: [] as Warehouse[] })),
    ])
      .then(([productsRes, vendorsRes, costCentersRes, projectsRes, warehousesRes]) => {
        setProducts((productsRes.products || []).filter((p) => p.is_active !== false));
        setVendors((vendorsRes.vendors || []).filter((v) => v.is_active !== false));
        setCostCenters((costCentersRes.costCenters || []).filter((c) => c.isActive !== false));
        setProjects(projectsRes || []);
        setWarehouses(warehousesRes.warehouses || []);
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  // In edit mode, load the requisition once reference data is available so
  // Autocomplete values can be matched to the loaded product/vendor options.
  useEffect(() => {
    if (!isEdit || !id || loadingRefData) return;
    setLoadingRequisition(true);
    api
      .get<{ requisition: RequisitionFromApi }>(`/purchase-requisitions/${id}`)
      .then(({ requisition }) => {
        setRequisitionDate(requisition.requisition_date ? requisition.requisition_date.slice(0, 10) : todayISO());
        setRequisitionType(requisition.requisition_type);
        setDepartment(requisition.department || '');
        setPriority(requisition.priority);
        setRequiredByDate(requisition.required_by_date ? requisition.required_by_date.slice(0, 10) : '');
        setCostCenterId(requisition.cost_center_id ? String(requisition.cost_center_id) : '');
        setProjectId(requisition.project_id ? String(requisition.project_id) : '');
        setWarehouseId(requisition.warehouse_id ? String(requisition.warehouse_id) : '');
        setJustification(requisition.justification || '');
        setStatus(requisition.status);
        setItems(
          (requisition.items || []).map((it) => ({
            key: nextDraftKey(),
            itemId: it.id,
            product: products.find((p) => String(p.id) === String(it.product_id)) || null,
            quantity: String(it.quantity ?? ''),
            uom: it.uom || '',
            estimatedUnitCost: String(it.estimated_unit_cost ?? ''),
            specification: it.specification || '',
            preferredVendor: it.preferred_vendor_id
              ? vendors.find((v) => String(v.id) === String(it.preferred_vendor_id)) || null
              : null,
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load requisition'))
      .finally(() => setLoadingRequisition(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRefData]);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (key: string) => setItems((prev) => prev.filter((it) => it.key !== key));
  const updateItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleProductChange = (key: string, product: Product | null) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        return {
          ...it,
          product,
          uom: it.uom || product?.unit || '',
          estimatedUnitCost:
            it.estimatedUnitCost || (product?.purchase_price ? String(product.purchase_price) : it.estimatedUnitCost),
        };
      })
    );
  };

  const estimatedTotal = useMemo(
    () => items.reduce((sum, it) => sum + toNum(it.quantity) * toNum(it.estimatedUnitCost), 0),
    [items]
  );

  const canEdit = !isEdit || status === 'DRAFT' || status === 'RETURNED';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!requisitionDate) errs.push('Requisition date is required.');
    if (items.length === 0) errs.push('At least one line item is required.');
    items.forEach((it, idx) => {
      if (!it.product) errs.push(`Line ${idx + 1}: a product is required.`);
      if (toNum(it.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        requisitionDate,
        requisitionType,
        department: department.trim() || null,
        costCenterId: costCenterId ? Number(costCenterId) : null,
        projectId: projectId ? Number(projectId) : null,
        warehouseId: warehouseId ? Number(warehouseId) : null,
        requiredByDate: requiredByDate || null,
        priority,
        justification: justification.trim() || null,
        items: items.map((it) => ({
          productId: it.product?.id,
          quantity: toNum(it.quantity),
          uom: it.uom.trim() || undefined,
          estimatedUnitCost: toNum(it.estimatedUnitCost),
          specification: it.specification.trim() || undefined,
          preferredVendorId: it.preferredVendor?.id || undefined,
        })),
      };

      const response =
        isEdit && id
          ? await api.put<{ requisition: { id: number } }>(`/purchase-requisitions/${id}`, payload)
          : await api.post<{ requisition: { id: number } }>('/purchase-requisitions', payload);

      navigate(`/procurement/requisitions/${response.requisition.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save purchase requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingRequisition;

  if (loading) {
    return (
      <Box sx={{ bgcolor: P.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const backTo = isEdit && id ? `/procurement/requisitions/${id}` : '/procurement/requisitions';
  const statusLabel = isEdit && status ? status : 'DRAFT';
  const statusTone = statusLabel === 'DRAFT' ? 'draft' : statusLabel === 'RETURNED' ? 'amber' : 'muted';
  const totalQty = items.reduce((s, it) => s + toNum(it.quantity), 0);

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
      <WorkspaceHeader
        onBack={() => navigate(backTo)}
        title={isEdit ? 'Edit Purchase Requisition' : 'New Purchase Requisition'}
        subtitle="Describe what is needed, why, and by when — this becomes the source document for the purchase order."
        chip={<StatusPill label={statusLabel} tone={statusTone as any} />}
        actions={
          canEdit && (
            <>
              <Button variant="outlined" onClick={() => navigate(backTo)} disabled={submitting} sx={outBtn}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <VerifiedUser sx={{ fontSize: 18 }} />}
                onClick={handleSubmit}
                disabled={submitting}
                sx={priBtn}
              >
                {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Requisition'}
              </Button>
            </>
          )
        }
      />

      <Box sx={{ px: PX, pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            This requisition is {status} and can no longer be edited.
          </Alert>
        )}
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {validationErrors.map((e) => (
              <Box key={e}>{e}</Box>
            ))}
          </Alert>
        )}

        {/* Metadata */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Requisition Details</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Requisition Date" required />
              <TextField type="date" fullWidth size="small" value={requisitionDate} onChange={(e) => setRequisitionDate(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Requisition Type" />
              <TextField select fullWidth size="small" value={requisitionType} onChange={(e) => setRequisitionType(e.target.value as RequisitionType)} disabled={!canEdit} sx={fieldSx}>
                {REQUISITION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Department" />
              <TextField fullWidth size="small" value={department} onChange={(e) => setDepartment(e.target.value)} disabled={!canEdit} placeholder="e.g. Production" sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Priority" />
              <TextField select fullWidth size="small" value={priority} onChange={(e) => setPriority(e.target.value as RequisitionPriority)} disabled={!canEdit} sx={fieldSx}>
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Required By" />
              <TextField type="date" fullWidth size="small" value={requiredByDate} onChange={(e) => setRequiredByDate(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Cost Center" />
              <TextField select fullWidth size="small" value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>
                {costCenters.length === 0 ? (
                  <MenuItem value="" disabled>No cost centers configured</MenuItem>
                ) : (
                  costCenters.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.code} — {c.name}</MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Project" />
              <TextField select fullWidth size="small" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>
                {projects.length === 0 ? (
                  <MenuItem value="" disabled>No projects configured</MenuItem>
                ) : (
                  projects.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>{p.project_code} — {p.project_name}</MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Warehouse" />
              <TextField select fullWidth size="small" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>
                {warehouses.length === 0 ? (
                  <MenuItem value="" disabled>No warehouses configured</MenuItem>
                ) : (
                  warehouses.map((w) => (
                    <MenuItem key={w.id} value={String(w.id)}>{w.warehouseCode ? `${w.warehouseCode} — ${w.warehouseName}` : w.warehouseName}</MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Justification" />
              <TextField fullWidth size="small" multiline minRows={2} value={justification} onChange={(e) => setJustification(e.target.value)} disabled={!canEdit} placeholder="Why is this needed? What happens if it isn't approved?" sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* Line items — full width */}
        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${P.line}` }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Line Items</Typography>
            {canEdit && (
              <Button size="small" startIcon={<AddCircle sx={{ fontSize: 18 }} />} onClick={addItem} sx={{ ...outBtn, height: 34 }}>Add Item</Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 40 }}><Typography sx={thTitle}>#</Typography></TableCell>
                  <TableCell sx={thSx}><Typography sx={thTitle}>Product <Box component="span" sx={{ color: P.red }}>*</Box></Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 90 }}><Typography sx={thTitle}>Qty</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 90 }}><Typography sx={thTitle}>UOM</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Est. Unit Cost</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Line Total</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 190 }}><Typography sx={thTitle}>Specification</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 200 }}><Typography sx={thTitle}>Preferred Vendor</Typography></TableCell>
                  {canEdit && <TableCell sx={{ ...thSx, width: 48 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={it.key} sx={{ '& td': { borderBottom: `1px solid ${P.tline}`, py: 0.5 } }}>
                    <TableCell sx={{ color: P.muted, fontSize: '13px' }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Autocomplete size="small" options={products} value={it.product} onChange={(_, v) => handleProductChange(it.key, v)} getOptionLabel={(o) => `${o.product_name}${o.hsn_code ? ` (${o.hsn_code})` : ''}`} isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)} disabled={!canEdit} renderInput={(params) => <TextField {...params} placeholder="Select product" sx={gridInput} />} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={it.quantity} onChange={(e) => updateItem(it.key, { quantity: e.target.value })} inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={it.uom} onChange={(e) => updateItem(it.key, { uom: e.target.value })} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={it.estimatedUnitCost} onChange={(e) => updateItem(it.key, { estimatedUnitCost: e.target.value })} inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: P.strong, whiteSpace: 'nowrap', fontSize: '13.5px' }}>
                      {formatIndianNumber(toNum(it.quantity) * toNum(it.estimatedUnitCost))}
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={it.specification} onChange={(e) => updateItem(it.key, { specification: e.target.value })} disabled={!canEdit} fullWidth placeholder="Spec / grade" sx={gridInput} />
                    </TableCell>
                    <TableCell>
                      <Autocomplete size="small" options={vendors} value={it.preferredVendor} onChange={(_, v) => updateItem(it.key, { preferredVendor: v })} getOptionLabel={(o) => o.company_name} isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)} disabled={!canEdit} renderInput={(params) => <TextField {...params} placeholder="Any vendor" sx={gridInput} />} />
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => removeItem(it.key)} disabled={items.length === 1} sx={{ color: P.muted, '&:hover': { color: P.red } }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Totals + status strip */}
        <Box sx={{ mb: 1.5 }}>
          <TotalsStrip
            cells={[
              { label: 'Line Items', value: items.length },
              { label: 'Total Quantity', value: formatIndianNumber(totalQty) },
              { label: 'Estimated Value', value: `${formatIndianNumber(estimatedTotal)}`, tone: 'primary' },
              { label: 'Status', value: statusLabel },
            ]}
          />
        </Box>

        {/* Validation strip */}
        <ValidationStrip
          items={[
            { ok: !!requisitionDate, label: 'Requisition date set' },
            { ok: items.length > 0, label: 'At least one line' },
            { ok: items.every((it) => !!it.product), label: 'All lines have a product' },
            { ok: items.every((it) => toNum(it.quantity) > 0), label: 'All quantities valid' },
          ]}
        />
      </Box>
    </Box>
  );
};

export default PurchaseRequisitionFormPage;
